import React, { useState } from 'react'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import TimelineItem from '@mui/lab/TimelineItem'
import TimelineSeparator from '@mui/lab/TimelineSeparator'
import TimelineConnector from '@mui/lab/TimelineConnector'
import TimelineDot from '@mui/lab/TimelineDot'
import Typography from '@mui/material/Typography'
import { formatLocal, formatUTC } from 'utils/date'
import Pagination from '@mui/material/Pagination'
import { translate } from 'locales'

// Styles
import { Title, Loading, CardDate } from 'components'
import noData from 'assets/images/no-data.svg'
import * as S from './styles'
import {
  Timeline,
  TimelineContent,
  TimelineOppositeContent,
  AccordionHeadingDiv,
  AccordionHeadingTypography,
} from '../timelineStyles'

import TimeLineResume from './TimeLineResume'
import useFetch from 'hooks/useFetch'

const History: React.FunctionComponent<{
  doctorName: string
  scheduleId: string
  userId: string
}> = ({ doctorName, scheduleId, userId }) => {
  // States
  const [page, setPage] = useState(1)

  const { data: medicalHistory, isLoading } = useFetch({
    url: `/doctors/v2/medical-record/user/${userId}?page=${page}&limit=5`,
    condition: !!userId,
    options: {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  })

  return (
    <S.SectionContent>
      {isLoading && (
        <Loading title={translate('medicalRecord.loadingHistoric')} />
      )}

      <span>{formatLocal(new Date(), 'EEEE, dd/MM/yyyy')}</span>
      <p>{`${translate('medicalRecord.by')} ${doctorName}`}</p>

      <div className="subsection">
        <Title text={translate('medicalRecord.historicPatiente')} />
        <div className="subsection-item">
          <Timeline position="right">
            {medicalHistory?.medicalRecords?.length === 0 && (
              <S.NoData>
                <img
                  src={noData}
                  alt="Icone simbolizando que não existe histórico do paciente"
                />
                <span>{translate('medicalRecord.notMedicalRecord')}</span>
              </S.NoData>
            )}

            {medicalHistory?.medicalRecords &&
              medicalHistory?.medicalRecords?.map((record: any) => (
                <TimelineItem key={record.id}>
                  <TimelineOppositeContent>
                    <CardDate
                      day={formatUTC(record.created_at, 'dd')}
                      month={formatUTC(record.created_at, 'MMM')}
                      year={formatUTC(record.created_at, 'yyyy')}
                    />
                  </TimelineOppositeContent>
                  <TimelineSeparator>
                    <TimelineDot />
                    <TimelineConnector />
                  </TimelineSeparator>
                  <TimelineContent>
                    <Accordion>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1a-content"
                        id={record.id}
                      >
                        <AccordionHeadingDiv
                          style={{
                            marginTop: 0,
                            display: 'flex',
                            flex: 1,
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <AccordionHeadingTypography>
                            {`${translate('medicalRecord.by')} Dr(a). ${
                              record?.scheduling?.doctor?.name
                            }`}
                            <Typography>
                              {`${translate('schedule.scheduleDate')}: ${formatLocal(record.scheduling.from_date_time, 'dd/MM/yyyy')}`}
                            </Typography>
                          </AccordionHeadingTypography>
                        </AccordionHeadingDiv>
                        <div>
                          <Typography>
                            {` (${record?.scheduling?.specialty_name})`}
                          </Typography>
                        </div>
                      </AccordionSummary>
                      <AccordionDetails style={{ display: 'block' }}>
                        <TimeLineResume
                          doctorName=""
                          scheduleUUID={scheduleId}
                          record={record}
                        />
                      </AccordionDetails>
                    </Accordion>
                  </TimelineContent>
                </TimelineItem>
              ))}
          </Timeline>
          <S.ContentPagination>
            {medicalHistory?.totalPages > 1 && (
              <Pagination
                count={medicalHistory?.totalPages}
                onChange={(
                  event: React.ChangeEvent<unknown>,
                  value: number
                ) => {
                  setPage(value)
                }}
              />
            )}
          </S.ContentPagination>
        </div>
      </div>
    </S.SectionContent>
  )
}

export default History
